import { FormHelperText, Grid, Typography } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CSS from 'csstype';
import Fuse from 'fuse.js';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';

import { IFilterPillIcon } from '~/src/dash/components/filters/common';
import {
  ActiveResourceFilter,
  IQuickFilter,
  MaterialSearchDropdownMultiList,
  SortByMode,
} from '~/src/dash/components/material-search-dropdown-multi-list';
import { Col, Cols, Text, usePublicaTranslation } from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatForClassname } from '~/src/dash/utils/format';

interface IProps {
  // SEARCH PROPS
  //
  // Function to get the value from a search element. Used for the search
  // default: take the value from the title field
  getValueFromSearchElement?: (src: any) => string;
  fontSize?: string;
  isFontBold?: boolean;
  // font color when nothing is selected
  fontColor?: string;
  // Fuzzy search options, doc here: https://fusejs.io/api/options.html
  // default value is:
  // {
  // keys: [{ name: 'ID', weight: 2 }, 'title'],
  // minMatchCharLength: 1,
  // threshold: 0.2,
  // useExtendedSearch: false,
  // ignoreLocation: true,
  // includeScore: true,
  // }
  fuzzySearchOptions?: Fuse.IFuseOptions<any>;

  // title to show for X ____ Selected
  menuTitle: string;
  // title override will show this text all the time
  menuTitleOverride?: string;
  // menuTitle if only one selected
  singularTitle?: string;
  // Custom prefix for title - default is 'Select'
  titlePrefix?: string;
  // Input list
  list: IMenuList;
  // Enable Select All button?
  // default=false
  selectAll?: boolean;
  // Is MultiSelect allowed?
  // default=true
  enableMultiSelect?: boolean;
  initialDisplayLimit?: number;
  // On change, input is modified list (with .selected set and up to date)
  onChange: (values: IMenuList) => void;
  // On Accept OR click outside
  handleMenuAcceptClick?: (values: IMenuList) => void;

  // Allow/Deny logic PROPS
  allowDenyLogicEnabled?: boolean;
  onChangeAllowDenyToggle?: (isAllow: boolean) => void;
  isAllowedSelected?: boolean;

  // Some fields are default "All" even if nothing is selected, like countries filter on campaigns or bidders. Used to show "All" by default when menu closed
  isNoneSelectedAllSelected?: boolean;
  helperText?: string;

  // Height of the list
  // default: 400px
  maxHeight?: number;

  // width of the component
  width?: string;
  // use Icon as a button to open the dropdown
  icon?: JSX.Element;
  // disabled turns off opening of menu
  disabled?: boolean;
  // disabled adds tooltip to disabled item in list
  disabledTooltip?: string;
  // disables search input box
  disableSearch?: boolean;
  // disables quick filter
  disableSortBy?: boolean;

  // quick filter related prop
  quickFilterOptions?: Array<{ title: string; optionItems: IMenuList; type: string }>;
  overrideActiveResourceFilter?: ActiveResourceFilter;
  disableAlphaToggle?: boolean;

  // enabled to make list read only
  enableReadOnly?: boolean;
  // limits the number of selections that can be made on the menu, only enabled if multiSelect is enabled and selectLimit != -1
  selectLimit?: number;
  // use this to enable an external opening of this dropdown
  forceOpen?: boolean;
  // when only item is selected, show the number selected or show the title
  onlyShowNumberWhenClosed?: boolean;

  // function that is called when Menu is opened
  onOpen?: () => void;
  loading?: boolean;

  // turns on left or right side dropdown
  leftRightDropdownEnabled?: boolean;
  // turns on responsive containment of modal based on position
  responsivePositionEnabled?: boolean;
  // text for a top item in a menu and a function passed to handle if it is clicked
  topListItemText?: string;
  topListItemClickHandler?: () => void;
  // enables fixed for filter button dropdown
  enableFixed?: boolean;

  // modal button next to dropdown
  showModalButton?: boolean;
  modalButton?: JSX.Element;
  overrideDefaultSortMode?: SortByMode;

  // keeps the dropdown open even on clicking outside if set to true
  keepDropdownOpen?: boolean;

  // describes selected as being excluded
  isSelectionExcluded?: boolean;

  // For keeping creatives with the same advertiser as the parent order on the top of the list
  orderAdvertiserID?: number;

  // shows ID prefix in title
  showPrefixID?: boolean;
  disableBottomBorder?: boolean;

  // hide dropdown and only show modal button
  showDropdown?: boolean;

  // prefix icon props
  pillIcon?: IFilterPillIcon;
  // do not display 'selected' text
  disableSelectedText?: boolean;
}

// gets the default version of a specified menu list
export const getDefaultMenuList = (menuList: IMenuList, defaultValue: boolean) => {
  // clone the menu list
  const clonedMenuList = cloneDeep(menuList);
  // sets all selecteds to specified default boolean
  Object.values(clonedMenuList).forEach((item: IListItem) => (item.selected = defaultValue));
  return clonedMenuList;
};

export const MaterialSearchDropdownMulti = (props: IProps) => {
  // Props
  const {
    disableAlphaToggle,
    disableBottomBorder,
    disabled,
    disabledTooltip,
    disableSearch,
    disableSelectedText,
    disableSortBy,
    enableFixed,
    enableMultiSelect,
    enableReadOnly,
    fontColor,
    fontSize,
    forceOpen,
    handleMenuAcceptClick,
    helperText,
    icon,
    initialDisplayLimit,
    isAllowedSelected,
    isFontBold,
    isNoneSelectedAllSelected,
    isSelectionExcluded,
    keepDropdownOpen,
    leftRightDropdownEnabled,
    list,
    loading,
    maxHeight,
    menuTitle,
    menuTitleOverride,
    modalButton,
    onChange,
    onlyShowNumberWhenClosed,
    onOpen,
    orderAdvertiserID,
    overrideActiveResourceFilter,
    overrideDefaultSortMode,
    pillIcon,
    quickFilterOptions,
    responsivePositionEnabled,
    selectAll,
    selectLimit,
    showDropdown,
    showModalButton,
    singularTitle,
    showPrefixID,
    titlePrefix,
    topListItemClickHandler,
    topListItemText,
    width,
  } = props;

  const { t } = usePublicaTranslation();

  // State
  const mainDropdownMenuRef = useRef<HTMLDivElement>(null);
  const mainDropdownContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isHover, setIsHover] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);

  const quickFilterMenuRef = useRef<HTMLDivElement>(null);
  const quickFilterButtonRef = useRef<HTMLDivElement>(null);
  // QFM= Quick Filter Menu
  const [isOpenQFMenu, setisOpenQFMenu] = useState<boolean>(false);

  const sortByMenuRef = useRef<HTMLDivElement>(null);
  const sortByButtonRef = useRef<HTMLDivElement>(null);
  const [isOpenSort, setIsOpenSort] = useState<boolean>(false);

  const quickFilterDropdownMenuRef = useRef<HTMLDivElement>(null);
  // QFD= Quick Filter Dropdown
  const [isOpenQFDropdown, setisOpenQFDropdown] = useState<boolean>(false);

  const [quickFilters, setQuickFilters] = useState<
    Array<{ title: string; optionItems: IMenuList; type: string }> | undefined
  >(quickFilterOptions);

  // this is for component such as BidderReportFormView, which for some reasons takes extra renders to get the quickFilterOptions, which causes MaterialSearchDropdownMulti to render WITHOUT quickFilterOptions. This ensure that we do have quickFilterOptions.
  if (quickFilters !== undefined && quickFilters.length === 0) {
    if (!!quickFilterOptions && quickFilterOptions.length > 0) {
      setQuickFilters(quickFilterOptions);
    }
  }

  // filter options (example: Group, Label, etc) will open up the final dropdowns
  const quickFilterMenuItems = [] as IQuickFilter[];

  if (quickFilters !== undefined) {
    for (let i = 0; i < quickFilters.length; i++) {
      const filterOption: IQuickFilter = {
        title: quickFilters[i].title,
        optionItems: quickFilters[i].optionItems,
        show: false,
        type: quickFilters[i].type,
      };
      quickFilterMenuItems.push(filterOption);
    }
  } else if (quickFilterOptions) {
    for (let i = 0; i < quickFilterOptions.length; i++) {
      const filterOption: IQuickFilter = {
        title: quickFilterOptions[i].title,
        optionItems: quickFilterOptions[i].optionItems,
        show: false,
        type: quickFilterOptions[i].type,
      };
      quickFilterMenuItems.push(filterOption);
    }
  }

  useEffect(() => {
    // in pages that contains multiple MaterialSearchDropdownMulti, such as monitor/new, we want to make sure that switching resources (menuTitle) will reset filterOptionItems
    if (quickFilterOptions) {
      if (quickFilterMenuItems.length === 0) {
        for (let i = 0; i < quickFilterOptions.length; i++) {
          const filterOption: IQuickFilter = {
            title: quickFilterOptions[i].title,
            optionItems: quickFilterOptions[i].optionItems,
            show: false,
            type: quickFilterOptions[i].type,
          };
          quickFilterMenuItems.push(filterOption);
        }
      }
    } else {
      quickFilterMenuItems.splice(0, quickFilterMenuItems.length);
      setFilterOptionItems(quickFilterMenuItems);
    }
  }, [menuTitle]);

  const [filterOptionItems, setFilterOptionItems] = useState<IQuickFilter[]>(quickFilterMenuItems);

  if (filterOptionItems !== undefined && filterOptionItems.length === 0) {
    if (quickFilterMenuItems.length > 0) {
      setFilterOptionItems(quickFilterMenuItems);
    }
  }

  const handleShow = (title: string) => {
    const elementsIndex = filterOptionItems.findIndex((element) => element.title === title);
    const newFilterOptionItems = [...quickFilterMenuItems];
    newFilterOptionItems[elementsIndex] = {
      ...newFilterOptionItems[elementsIndex],
      show: title === 'noHover' ? false : !newFilterOptionItems[elementsIndex].show,
    };
    newFilterOptionItems.map((newFilterOptionItem, i) => {
      if (i !== elementsIndex) {
        newFilterOptionItem.show = false;
      }
    });
    setFilterOptionItems(newFilterOptionItems);
  };

  // closes component when clicked outside, opens component when clicked on
  function handleClickOutside(this: Document, e: MouseEvent): void {
    if (
      quickFilterDropdownMenuRef.current !== null &&
      !quickFilterDropdownMenuRef.current.contains(e.target as Node)
    ) {
      const newfilterOptionItems = [...filterOptionItems];
      newfilterOptionItems.map((ele) => {
        ele.show = false;
      });
      setFilterOptionItems(newfilterOptionItems);
    }

    // stops the closing of dropdown
    if (!keepDropdownOpen) {
      if (
        mainDropdownContainerRef.current !== null &&
        mainDropdownContainerRef.current.contains(e.target as Node)
      ) {
        handleMenuOpen();
      } else if (
        mainDropdownMenuRef.current !== null &&
        !mainDropdownMenuRef.current.contains(e.target as Node)
      ) {
        handleMenuAccept();
      }
    }

    if (
      quickFilterButtonRef.current !== null &&
      quickFilterButtonRef.current.contains(e.target as Node) &&
      !isOpenQFMenu
    ) {
      handleQFMenuOpen();
    } else if (
      quickFilterMenuRef.current !== null &&
      !quickFilterMenuRef.current.contains(e.target as Node)
    ) {
      handleQFMenuAccept();
    }

    if (
      sortByButtonRef.current !== null &&
      sortByButtonRef.current.contains(e.target as Node) &&
      !isOpenSort
    ) {
      handleSortByMenuOpen();
    } else if (
      sortByMenuRef.current !== null &&
      !sortByMenuRef.current.contains(e.target as Node)
    ) {
      handleSortByMenuAccept();
    }
  }

  // if the ids in the list change, we should force a re-render
  const idsString = Object.values(list || {})
    .map((item: IListItem) => item.ID)
    .join(',');
  useEffect(() => {
    setCount(count + 1);
  }, [idsString]);

  // if using force open, handleMenuAcceptClick in parent component should have
  // a way to force closed
  useEffect(() => {
    setIsOpen(forceOpen === true);
  }, [forceOpen]);

  // sets up listener events to open and close dropdown menu
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  });

  const handleMenuOpen = (): void => {
    if (props === undefined || (!disabled && !loading)) {
      // When menu goes from not open to open, run the onOpen function if defined
      if (!isOpen && onOpen) {
        onOpen();
      }

      setIsOpen(true);
    }
  };

  // closes menu and updates list in parent component
  const handleMenuAccept = (listOverride?: IMenuList): void => {
    setIsOpen(false);
    if (handleMenuAcceptClick) {
      if (listOverride !== undefined) {
        handleMenuAcceptClick(listOverride);
      } else {
        handleMenuAcceptClick(list);
      }
    }
  };

  const handleQFMenuOpen = (): void => {
    if (props === undefined || !disabled) {
      // When Quick Filter menu goes from not open to open, run the onOpen function if defined
      if (!isOpenQFMenu && onOpen) {
        onOpen();
      }
      setisOpenQFMenu(true);
    }
  };

  // closes QF menu and updates list in parent component
  const handleQFMenuAccept = (): void => {
    setisOpenQFMenu(false);
  };

  const handleSortByMenuOpen = (): void => {
    if (props === undefined || !disabled) {
      // When Sort By menu goes from not open to open, run the onOpen function if defined
      if (!isOpenSort && onOpen) {
        onOpen();
      }
      setIsOpenSort(true);
    }
  };

  // closes QF menu and updates list in parent component
  const handleSortByMenuAccept = (): void => {
    setIsOpenSort(false);
  };

  const handleQFDropdownOpen = (): void => {
    if (props === undefined || !disabled) {
      // When Quick Filter Dropdown goes from not open to open, run the onOpen function if defined
      if (!isOpenQFDropdown && onOpen) {
        onOpen();
      }
      setisOpenQFDropdown(true);
    }
  };

  // closes QF menu and updates list in parent component
  const handleQFDropdownAccept = (): void => {
    setisOpenQFDropdown(false);
  };

  // determine num selected or all selected for UI
  // displays the name of the first selection if we only have 1 item vs the number
  const allSelected = Object.values(list || {}).filter((f) => f.selected);
  const numSelected = allSelected.length;
  let menuClosedDisplay = '';
  if (numSelected === 1 && allSelected.length >= 0) {
    if (onlyShowNumberWhenClosed) {
      menuClosedDisplay += t('FormatLabelWithCount', { label: menuTitle, count: numSelected });
    } else {
      menuClosedDisplay = allSelected[0].title;
    }
  } else if (isNoneSelectedAllSelected && numSelected === 0) {
    menuClosedDisplay += t('AllItemsSelected', { item: menuTitle });
  } else {
    menuClosedDisplay += t('FormatLabelWithCount', { label: menuTitle, count: numSelected });
  }

  const maxWidth = 480;

  const getResponsiveStylePosition = (): CSS.Properties => {
    if (
      responsivePositionEnabled &&
      mainDropdownContainerRef.current &&
      mainDropdownContainerRef.current.getBoundingClientRect
    ) {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;
      const midPoint = windowWidth / 2;
      const rightRange = maxWidth / 4;
      const leftPos = mainDropdownContainerRef.current.getBoundingClientRect().left;
      const rightPos = mainDropdownContainerRef.current.getBoundingClientRect().right;
      const currentPos = (leftPos + rightPos) / 2;
      const isNearCenter =
        currentPos > midPoint + maxWidth / 8 && currentPos < midPoint + maxWidth / 3;
      switch (true) {
        case isNearCenter:
          return { left: `${0 - maxWidth / 3}px` };
        case currentPos >= midPoint + rightRange:
          return { right: '0px' };
        default:
          return { left: '0px' };
      }
    }
    return { left: '0px' };
  };

  // determines if middle of the dropdown is in the left side or right side of the page
  // as well as the actual position
  // TODO: this positioning code should be refactored or replaced when building the new dropdowns
  const isBaseOnRightSide = (): [boolean, number, number] => {
    if (
      leftRightDropdownEnabled &&
      mainDropdownContainerRef.current &&
      mainDropdownContainerRef.current.getBoundingClientRect
    ) {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;
      const leftPos = mainDropdownContainerRef.current.getBoundingClientRect().left;
      const rightPos = mainDropdownContainerRef.current.getBoundingClientRect().right;
      const bottomPos = mainDropdownContainerRef.current.getBoundingClientRect().bottom;
      const currentPos = (leftPos + rightPos) / 2;
      return [currentPos > windowWidth / 2, rightPos, bottomPos];
    }
    return [false, 0, 0];
  };

  const getBorderBottom = (): string => {
    if (disableBottomBorder) {
      return '0px';
    } else if (isHover && !disabled && !loading) {
      return '2px solid black';
    }
    return '1px solid rgba(0, 0, 0, 0.42)';
  };
  const [isRightSide, rightPos, bottomPos] = isBaseOnRightSide();
  return (
    <Grid style={{ height: '100%', display: 'flex', alignItems: 'baseline', width: '100%' }}>
      {icon !== undefined
        ? showDropdown !== false && (
            <Grid
              container
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              direction='row'
              ref={mainDropdownContainerRef}
            >
              {icon}
            </Grid>
          )
        : showDropdown !== false && (
            <Grid
              container
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              style={{
                borderBottom: getBorderBottom(),
                maxWidth: width || 450,
                maxHeight: 22,
                width,
              }}
              justifyContent='space-between'
              direction='row'
              ref={mainDropdownContainerRef}
            >
              {pillIcon ? (
                <Cols
                  paddingRight='12px'
                  paddingLeft='2px'
                  gap='4px'
                  style={{ alignItems: 'center' }}
                >
                  <Col style={{ verticalAlign: 'middle' }}>
                    <pillIcon.SvgComponent
                      color={numSelected > 0 ? publicaColors.jungleGreen : publicaColors.black}
                      style={pillIcon.style}
                    />
                  </Col>
                  <Col>
                    <Text
                      extraSmall
                      bold
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: disabled || loading ? '#cecdcf' : fontColor || 'black',
                        whiteSpace: 'nowrap',
                        ...(pillIcon.labelStyle || {}),
                      }}
                    >
                      {loading
                        ? t('LoadingEllipsis')
                        : numSelected === 0 &&
                          (!isNoneSelectedAllSelected ||
                            menuTitleOverride === t('SelectItem', { item: t('DeviceEnvironment') }))
                        ? menuTitle
                        : menuClosedDisplay}
                    </Text>
                  </Col>
                </Cols>
              ) : (
                <>
                  <Grid
                    item
                    lg={10}
                    md={10}
                    sm={10}
                    xs={10}
                    className={
                      'pb-material-search-dropdown-multi_griditem-' + formatForClassname(menuTitle)
                    }
                  >
                    <Typography
                      noWrap
                      style={{
                        fontSize: fontSize || '14px',
                        fontWeight: isFontBold ? 500 : 'inherit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: disabled || loading ? '#cecdcf' : fontColor || 'black',
                        paddingTop: disableBottomBorder ? '2px' : '',
                      }}
                    >
                      {loading
                        ? t('LoadingEllipsis')
                        : numSelected === 0 &&
                          (!isNoneSelectedAllSelected ||
                            menuTitleOverride === t('SelectItem', { item: t('DeviceEnvironment') }))
                        ? menuTitleOverride && menuTitleOverride !== ''
                          ? menuTitleOverride
                          : `${titlePrefix || t('Select')} ${menuTitle}`
                        : menuClosedDisplay}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    container
                    lg={2}
                    md={2}
                    sm={2}
                    xs={2}
                    justifyContent='flex-end'
                    alignItems='flex-start'
                  >
                    <Grid item>
                      <ArrowDropDownIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          )}
      <MaterialSearchDropdownMultiList
        // standard dropdown props
        isAllowedSelected={isAllowedSelected}
        onChange={onChange}
        selectAll={selectAll}
        enableMultiSelect={enableMultiSelect}
        maxHeight={maxHeight}
        width={width}
        menuTitle={menuTitle}
        list={list || {}}
        initialDisplayLimit={initialDisplayLimit}
        handleMenuOpen={handleMenuOpen}
        isOpen={isOpen}
        handleMenuAccept={handleMenuAccept}
        mainDropdownMenuRef={mainDropdownMenuRef}
        quickFilterMenuRef={quickFilterMenuRef}
        sortByMenuRef={sortByMenuRef}
        sortByButtonRef={sortByButtonRef}
        quickFilterDropdownMenuRef={quickFilterDropdownMenuRef}
        quickFilterButtonRef={quickFilterButtonRef}
        enableReadOnly={enableReadOnly}
        disableSearch={disableSearch}
        disableSortBy={disableSortBy}
        disabledTooltip={disabledTooltip}
        selectLimit={selectLimit}
        loading={loading}
        isRightSide={isRightSide}
        rightPos={rightPos}
        bottomPos={bottomPos}
        responsivePositionEnabled={responsivePositionEnabled}
        responsiveStylePosition={getResponsiveStylePosition()}
        topListItemText={topListItemText}
        topListItemClickHandler={topListItemClickHandler}
        enableFixed={enableFixed}
        isOpenQFMenu={isOpenQFMenu}
        isOpenSort={isOpenSort}
        isOpenQFDropdown={isOpenQFDropdown}
        quickFilterMenuItems={filterOptionItems}
        handleQFDropdownOpen={handleQFDropdownOpen}
        handleQFDropdownAccept={handleQFDropdownAccept}
        handleClickOutside={handleClickOutside}
        handleShow={handleShow}
        onChangeQuickFilter={(
          newQuickFilters: Array<{ title: string; optionItems: IMenuList; type: string }>,
        ) => {
          setQuickFilters(newQuickFilters);
        }}
        setFilterOptionItems={setFilterOptionItems}
        overrideDefaultSortMode={overrideDefaultSortMode}
        keepDropdownOpen={keepDropdownOpen}
        orderAdvertiserID={orderAdvertiserID || undefined}
        showPrefixID={!!showPrefixID}
        overrideActiveResourceFilter={overrideActiveResourceFilter}
        disableAlphaToggle={disableAlphaToggle}
        leftRightDropdownEnabled={leftRightDropdownEnabled}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {showModalButton && modalButton}
    </Grid>
  );
};
